import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { Button, Icon } from '@sgme/ui';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { CSSProperties } from 'react';
import { type FormatNumberOptions, FormattedNumber } from 'react-intl';

import { Expiry } from '@/App/tiles/Option/fields/Expiry.tsx';
import { Strike } from '@/App/tiles/Option/mainFields/Strike.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { ComMargin } from '@/App/tiles/Option/strategy/fields/ComMargin.tsx';
import { HedgeAmount } from '@/App/tiles/Option/strategy/fields/HedgeAmount.tsx';
import { HedgeRate } from '@/App/tiles/Option/strategy/fields/HedgeRate.tsx';
import { Notional } from '@/App/tiles/Option/strategy/fields/Notional.tsx';
import { Premium } from '@/App/tiles/Option/strategy/fields/Premium.tsx';
import { TriggerEndDate1, TriggerEndDate2 } from '@/App/tiles/Option/strategy/fields/TriggerEndDate.tsx';
import {
  FirstTrigger1,
  FirstTrigger2,
  SecondTrigger1,
  SecondTrigger2,
} from '@/App/tiles/Option/strategy/fields/Triggers.tsx';
import { TriggerStartDate1, TriggerStartDate2 } from '@/App/tiles/Option/strategy/fields/TriggerStartDate.tsx';
import { TriggerType } from '@/App/tiles/Option/strategy/fields/TriggerType.tsx';
import { Type } from '@/App/tiles/Option/strategy/fields/Type.tsx';
import { Volatility } from '@/App/tiles/Option/strategy/fields/Volatility.tsx';
import { handleFieldTab } from '@/App/tiles/Option/strategy/fieldTabNavigation.ts';
import type { StrategyField } from '@/App/tiles/Option/strategy/types.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import {
  selectOptionCurrency,
  selectOptionHedge,
  selectOptionLegsProperties,
} from '@/store/option/selectors/optionSelectors.ts';
import { selectCurrencyPairInfo } from '@/store/option/selectors/selectors.ts';
import {
  type LegId,
  type LegsData,
  type OptionLegProperties,
  type OptionSide,
  premiumTenors,
} from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

interface FieldEditProps {
  field: StrategyField;
  tileId: string;
  leg: OptionLegProperties;
  quoteLeg: LegsData | undefined;
  legId: LegId;
  visible: boolean;
  style?: CSSProperties;
}

type FieldEditComponentProps = Omit<FieldEditProps, 'style'>;

export function FieldEdit(props: FieldEditProps) {
  const { style, ...fieldEditProps } = props;
  const { isExpired, shouldUseFirstTabOrder } = useOptionSelector((option) => ({
    isExpired: option.properties.rfs.status === 'EXPIRED',
    shouldUseFirstTabOrder: option.type === 'Vanilla' || option.type === 'Trigger' || option.type === 'Binary',
  }));

  return (
    <div
      data-strategy-field={props.field}
      data-strategy-leg={props.legId}
      onKeyDownCapture={shouldUseFirstTabOrder ? handleFieldTab : undefined}
      style={style}
      className={clsx('bg-lvl1 grid-cell', {
        'd-none': !props.visible,
        expired: isExpired,
      })}
    >
      <FieldEditComponent {...fieldEditProps} />
    </div>
  );
}

export function FieldEditComponent({ field, tileId, leg, quoteLeg, legId }: FieldEditComponentProps) {
  const { currency, currencyPairPrecision } = useOptionSelector((option, state) => {
    const { currency1, currency2 } = selectOptionCurrency(option);
    const notionalCurrencyIndex = selectOptionLegsProperties(option, legId)?.notionalCurrencyIndex;
    const { currencyPairPrecision } = selectCurrencyPairInfo(state, tileId);
    return {
      currency: notionalCurrencyIndex === 2 ? currency2 : currency1,
      currencyPairPrecision,
    };
  });
  const [checkOption] = useCheckOption();

  switch (field) {
    case 'side':
      return (
        <div className="d-flex">
          <input
            data-e2e="way-input"
            type="text"
            className="form-control form-control-sm bg-primary-alt shadow-none"
            readOnly
            value={leg.side}
          />
          <Button
            size="sm"
            purpose="flat"
            color="primary"
            iconPosition="start"
            className="bg-lvl1"
            data-e2e="way-button"
            onClick={() =>
              checkOption({
                tileId,
                legProperties: {
                  legId,
                  side: leg.side === 'Buy' ? 'Sell' : 'Buy',
                },
              })
            }
          >
            <Icon icon="swap_horiz" />
          </Button>
        </div>
      );
    case 'type':
      return leg.legProductName !== 'Binary' && <Type legId={leg.legId} />;
    case 'expiry':
      return <Expiry className="bg-lvl1" legId={legId} />;
    case 'strike':
      return leg.legProductName !== 'Binary' && <Strike legId={legId} dataE2e="strike" className="bg-lvl1" />;
    case 'notional':
      return <Notional className="bg-lvl1" tileId={tileId} legId={legId} />;
    case 'delivery':
      return (
        <DatePickerWithTenorNext
          className="bg-lvl1"
          data-e2e="delivery-date"
          onChange={(deliveryDate) => {
            checkOption({
              tileId,
              legProperties: { legId: legId, deliveryDate },
            });
          }}
          date={leg.deliveryDate}
          tenor={leg.deliveryDateTenor}
        />
      );
    case 'triggerType':
      return <TriggerType tileId={tileId} legId={legId} />;
    case 'firstTrigger1':
      return <FirstTrigger1 tileId={tileId} legId={legId} />;
    case 'secondTrigger1':
      return <SecondTrigger1 tileId={tileId} legId={legId} />;
    case 'TriggerStartDate1':
      return leg.barrier1?.nature === 'WINDOW' && <TriggerStartDate1 tileId={tileId} legId={legId} />;
    case 'TriggerEndDate1':
      return leg.barrier1?.nature === 'WINDOW' && <TriggerEndDate1 tileId={tileId} legId={legId} />;
    case 'firstTrigger2':
      return <FirstTrigger2 tileId={tileId} legId={legId} />;
    case 'secondTrigger2':
      return <SecondTrigger2 tileId={tileId} legId={legId} />;
    case 'TriggerStartDate2':
      return leg.barrier2?.nature === 'WINDOW' && <TriggerStartDate2 tileId={tileId} legId={legId} />;
    case 'TriggerEndDate2':
      return leg.barrier2?.nature === 'WINDOW' && <TriggerEndDate2 tileId={tileId} legId={legId} />;
    case 'premium':
      return (
        <Premium
          leg={leg}
          legData={quoteLeg}
          onChange={({ bid, ask }) => {
            checkOption({
              tileId,
              legProperties: { legId: legId, premiumBid: bid, premiumAsk: ask },
            });
          }}
        />
      );
    case 'premiumDate': {
      return (
        <DatePickerWithTenorNext
          className="bg-lvl1"
          onChange={(premiumPaymentDate) => {
            checkOption({
              tileId,
              legProperties: { legId: legId, premiumPaymentDate },
            });
          }}
          date={leg.premiumPaymentDate}
          tenor={leg.premiumPaymentDateTenor}
          allTenors={premiumTenors}
        />
      );
    }
    case 'markup':
      return (
        <ComMargin
          leg={leg}
          legData={quoteLeg}
          onChange={(markup) => {
            checkOption({ tileId, legProperties: { legId: legId, markup } });
          }}
        />
      );
    case 'volatility':
      return (
        <Volatility
          leg={leg}
          legData={quoteLeg}
          onChange={({ bid, ask }) => {
            checkOption({
              tileId,
              legProperties: {
                legId: legId,
                volatilityAsk: ask,
                volatilityBid: bid,
              },
            });
          }}
        />
      );
    case 'atmVolatility':
      return (
        <ReadOnlyField
          dataE2e="atmVolatility-text"
          value={quoteLeg?.midVolatility?.atmVolatilityMid}
          minimumFractionDigits={3}
        />
      );
    case 'forward':
      return (
        <ReadOnlyField
          side={leg.side}
          value={quoteLeg?.marketData.forward}
          minimumFractionDigits={currencyPairPrecision + 1}
        />
      );
    case 'hedgeRate':
      return <HedgeRate className="bg-lvl1" tileId={tileId} legId={legId} />;
    case 'hedgeAmount':
      return <HedgeAmount className="bg-lvl1" tileId={tileId} legId={legId} />;
    case 'hedgeDate':
      return <HedgeDate tileId={tileId} legId={legId} />;
    case 'swap':
      return <ReadOnlyField side={leg.side} value={quoteLeg?.marketData.swapPoint} minimumFractionDigits={2} />;
    case 'zc1':
      return <ReadOnlyField value={quoteLeg?.marketData.zeroCouponCcy1} minimumFractionDigits={4} />;
    case 'zc2':
      return <ReadOnlyField value={quoteLeg?.marketData.zeroCouponCcy2} minimumFractionDigits={4} />;
    case 'delta':
      return <ReadOnlyField value={quoteLeg?.greeks.deltaCcy1} unit={currency} maximumFractionDigits={0} />;
    case 'vega':
      return <ReadOnlyField value={quoteLeg?.greeks.vegaCcy1} unit={currency} maximumFractionDigits={0} />;
    case 'gamma':
      return <ReadOnlyField value={quoteLeg?.greeks.gammaCcy1} unit={currency} maximumFractionDigits={0} />;
    case 'theta':
      return <ReadOnlyField value={quoteLeg?.greeks.thetaCcy1} unit={currency} maximumFractionDigits={0} />;
    default:
      assertUnreachable(field, `Unknown field ${field}`);
  }
}

function HedgeDate({ legId }: { tileId: string; legId: LegId }) {
  const hedge = useOptionSelector((option) => selectOptionHedge(option, legId));
  return (
    <ReadOnlyField
      dataE2e="HedgePaymentDate-text"
      value={isDefined(hedge?.maturityDate) ? format(hedge.maturityDate, DATE_FORMAT_TEXT) : ''}
    />
  );
}

interface ReadOnlyFieldProps extends FormatNumberOptions {
  value: number | null | undefined | BidAsk<number | null | undefined> | string;
  unit?: string;
  dataE2e?: string;
  side?: OptionSide;
}

function ReadOnlyField({ value, unit, dataE2e, side, ...formatOptions }: ReadOnlyFieldProps) {
  return (
    <div data-e2e={dataE2e} className="monospace-numbers fs-6 d-flex align-items-center ps-1">
      <FormattedData value={value} {...formatOptions} side={side} unit={unit} />
    </div>
  );
}

function FormattedData({ value, side, unit, ...formatOptions }: ReadOnlyFieldProps) {
  if (isNotDefined(value)) {
    return '';
  }

  if (typeof value === 'string') {
    return (
      <>
        {value} {unit}
      </>
    );
  }
  if (typeof value === 'number') {
    return (
      <>
        <FormattedNumber value={value} {...formatOptions} /> {unit}
      </>
    );
  }

  return (
    <div>
      <span className={clsx({ 'text-secondary': side === 'Buy' })}>
        {value.bid && <FormattedNumber value={value.bid} {...formatOptions} />}
      </span>
      <span> / </span>
      <span className={clsx({ 'text-secondary': side === 'Sell' })}>
        {value.ask && <FormattedNumber value={value.ask} {...formatOptions} />}
      </span>
    </div>
  );
}
